import type { ReactNode } from "react";

import type { EquityPoint } from "../api/hooks.ts";
import { formatDay, formatR } from "../domain/format.ts";

/**
 * La curva de equity acumulada, en SVG escrito a mano (SPEC 3: nada de librerias
 * de graficos en `web/`).
 *
 * Es el heroe del panel y no un adorno: un "+4R acumulado" no distingue una
 * subida limpia de una montana rusa que acabo en el mismo sitio. La FORMA es la
 * lectura.
 *
 * La linea del cero va punteada porque es la unica referencia que importa, y la
 * peor caida se sombrea aparte: es el numero que decide si podrias haber
 * aguantado esta curva de verdad.
 */

const W = 720;
const H = 260;
const PAD = { left: 46, right: 18, top: 18, bottom: 30 };

export function EquityCurve(props: { points: readonly EquityPoint[] }): ReactNode {
  const { points } = props;

  if (points.length === 0) {
    return (
      <p className="muted">
        La curva aparece en cuanto cierres la primera operacion con stop.
      </p>
    );
  }

  /* El origen es el 0: la curva empieza donde empezaste, no en la primera
     operacion, o una racha inicial ganadora parecería el punto de partida. */
  const values = [0, ...points.map((point) => point.cumulativeR)];
  const top = Math.max(...values);
  const bottom = Math.min(...values);
  const span = top - bottom < 0.5 ? 0.5 : top - bottom;
  const max = top + span * 0.12;
  const min = bottom - span * 0.12;

  const x = (index: number): number =>
    PAD.left + (values.length === 1 ? 0 : (index / (values.length - 1)) * (W - PAD.left - PAD.right));
  const y = (value: number): number =>
    PAD.top + ((max - value) / (max - min)) * (H - PAD.top - PAD.bottom);

  const line = values.map((value, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(value)}`).join(" ");
  const area = `${line} L ${x(values.length - 1)} ${y(min)} L ${x(0)} ${y(min)} Z`;

  const last = values[values.length - 1] ?? 0;
  const winning = last >= 0;
  const color = winning ? "var(--up)" : "var(--down)";

  /* El punto mas hundido bajo su pico anterior: eso es la peor caida, y es el
     MISMO numero que da el servidor en el panel. Que la leyenda y el KPI digan
     cosas distintas seria peor que no pintar nada. */
  const trough = points.reduce(
    (worst, point, index) => (point.drawdownR < worst.value ? { value: point.drawdownR, index } : worst),
    { value: 0, index: -1 },
  );
  /* El pico del que cayo: el maximo acumulado hasta ese punto. */
  const peakBefore = points
    .slice(0, trough.index + 1)
    .reduce((best, point) => Math.max(best, point.cumulativeR), 0);

  const ticks = [max, (max + min) / 2, min].concat(min < 0 && max > 0 ? [0] : []);

  return (
    <figure className="equity-wrap">
      <svg className="equity" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel(points, last)}>
        <defs>
          <linearGradient id="equity-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.28 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
          </linearGradient>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              y1={y(tick)}
              x2={W - PAD.right}
              y2={y(tick)}
              style={{ stroke: "var(--border)" }}
              strokeWidth="1"
              strokeDasharray={tick === 0 ? "0" : "3 5"}
            />
            <text className="axis" x={PAD.left - 8} y={y(tick) + 4} textAnchor="end">
              {tick.toFixed(1)}
            </text>
          </g>
        ))}

        <path d={area} fill="url(#equity-fill)" />
        <path
          d={line}
          fill="none"
          style={{ stroke: color }}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {trough.index >= 0 && trough.value < 0 && (
          <line
            x1={x(trough.index + 1)}
            y1={y(peakBefore)}
            x2={x(trough.index + 1)}
            y2={y(values[trough.index + 1] ?? 0)}
            style={{ stroke: "var(--warn)" }}
            strokeWidth="2"
            strokeDasharray="4 3"
          />
        )}

        <circle
          cx={x(values.length - 1)}
          cy={y(last)}
          r="5"
          style={{ fill: "var(--surface)", stroke: color }}
          strokeWidth="3"
        />

        <text className="axis" x={PAD.left} y={H - 8}>
          {formatDay(points[0]?.tradeDateLocal)}
        </text>
        <text className="axis" x={W - PAD.right} y={H - 8} textAnchor="end">
          {formatDay(points[points.length - 1]?.tradeDateLocal)}
        </text>
      </svg>

      <figcaption className="equity-legend">
        <span>
          <i style={{ background: color }} /> R acumulado <b className="num">{formatR(last)}</b>
        </span>
        {trough.value < 0 && (
          <span>
            <i style={{ background: "var(--warn)" }} /> Peor caida{" "}
            <b className="num">{formatR(trough.value)}</b>
          </span>
        )}
        <span className="muted">{points.length} operaciones con R</span>
      </figcaption>
    </figure>
  );
}

function ariaLabel(points: readonly EquityPoint[], last: number): string {
  return `Curva de equity de ${points.length} operaciones. R acumulado ${formatR(last)}.`;
}
